import { chSupabase } from '../lib/supabase';
import type { ChPostMetrics } from '../lib/metrics';
import type { ChFollowers, ChPlatformMetrics, ChPostPrint } from '../types';

export interface ChClientAccessToken {
  id: string;
  project_id: string;
  token: string;
  label: string | null;
  mode: 'review' | 'view';
  expires_at: string | null;
  revoked: boolean;
  created_by: string | null;
  created_at: string;
}

export const chClientTokenService = {
  async getByProject(projectId: string): Promise<ChClientAccessToken[]> {
    const { data, error } = await chSupabase
      .from('ch_client_access_tokens')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as ChClientAccessToken[];
  },

  async create(projectId: string, label?: string, expiresAt?: string, mode: 'review' | 'view' = 'review'): Promise<ChClientAccessToken> {
    const { data: { user } } = await chSupabase.auth.getUser();
    const { data, error } = await chSupabase
      .from('ch_client_access_tokens')
      .insert({
        project_id: projectId,
        label: label || null,
        mode,
        expires_at: expiresAt || null,
        created_by: user?.id || null,
      })
      .select()
      .single();

    if (error) throw error;
    return data as ChClientAccessToken;
  },

  async revoke(id: string): Promise<void> {
    const { error } = await chSupabase
      .from('ch_client_access_tokens')
      .update({ revoked: true })
      .eq('id', id);

    if (error) throw error;
  },
};

export interface ClientReviewData {
  valid: boolean;
  error?: string;
  mode?: 'review' | 'view';
  project?: {
    id: string;
    name: string;
    slug: string;
    followers?: ChFollowers | null;
    followers_updated_at?: string | null;
  };
  branding?: {
    logo_url: string | null;
    primary_color: string;
    background_color: string;
    background_image_url: string | null;
    accent_color: string | null;
  } | null;
  posts?: ClientReviewPost[];
}

export interface ClientReviewPost {
  id: string;
  title: string;
  caption: string | null;
  platform: string;
  platforms?: string[] | null;
  content_type: string;
  scheduled_date: string | null;
  published_at?: string | null;
  media_url: string | null;
  media_urls?: string[] | null;
  thumbnail_url: string | null;
  status: string;
  client_decision: string | null;
  current_version: number;
  external_link?: string | null;
  metrics?: ChPostMetrics | null;
  metrics_by_platform?: ChPlatformMetrics | null;
  metrics_updated_at?: string | null;
  prints?: ChPostPrint[];
}

interface ReportEntry {
  id: string;
  status?: string;
  external_link?: string | null;
  published_at?: string | null;
  metrics?: ChPostMetrics | null;
  metrics_by_platform?: ChPlatformMetrics | null;
  metrics_updated_at?: string | null;
  prints?: ChPostPrint[];
}

/** Adds stats, prints and published links to the review data by post id. */
export function mergeReport(data: ClientReviewData, report: ReportEntry[]): ClientReviewData {
  if (!data.posts || report.length === 0) return data;
  const byId = new Map(report.map(r => [r.id, r]));
  return {
    ...data,
    posts: data.posts.map(p => {
      const r = byId.get(p.id);
      if (!r) return p;
      return {
        ...p,
        status: r.status || p.status,
        external_link: r.external_link ?? p.external_link ?? null,
        published_at: r.published_at ?? p.published_at ?? null,
        metrics: r.metrics ?? p.metrics ?? null,
        metrics_by_platform: r.metrics_by_platform ?? p.metrics_by_platform ?? null,
        metrics_updated_at: r.metrics_updated_at ?? p.metrics_updated_at ?? null,
        prints: r.prints ?? p.prints ?? [],
      };
    }),
  };
}

/** Logged-in client approving or asking for changes (checked server-side). */
export const chClientDecisionService = {
  async decide(
    postId: string,
    decision: 'approved' | 'changes_requested',
    note?: string
  ): Promise<{ success: boolean; error?: string; unavailable?: boolean }> {
    const { data, error } = await chSupabase.rpc('ch_client_decide', {
      p_post_id: postId,
      p_decision: decision,
      p_note: note ?? null,
    });
    if (error) {
      // PGRST202: function not created yet (permissions SQL not applied)
      if (error.code === 'PGRST202') return { success: false, unavailable: true };
      throw error;
    }
    return data as { success: boolean; error?: string };
  },
};

export const chClientReviewService = {
  async validateToken(token: string): Promise<ClientReviewData> {
    const { data, error } = await chSupabase.rpc('ch_validate_token_and_get_data', {
      p_token: token,
    });

    if (error) throw error;
    return data as ClientReviewData;
  },

  /** Stats and prints for a share link; empty until the stats SQL is applied. */
  async getReport(token: string): Promise<ReportEntry[]> {
    const { data, error } = await chSupabase.rpc('ch_token_get_report', { p_token: token });
    if (error) return [];
    const result = data as { success?: boolean; posts?: ReportEntry[] } | null;
    return result?.success ? result.posts || [] : [];
  },

  async approvePost(token: string, postId: string, actorName: string): Promise<{ success: boolean; error?: string }> {
    const { data, error } = await chSupabase.rpc('ch_token_approve_post', {
      p_token: token,
      p_post_id: postId,
      p_actor_name: actorName,
    });

    if (error) throw error;
    return data as { success: boolean; error?: string };
  },

  async requestChanges(token: string, postId: string, actorName: string, comment: string): Promise<{ success: boolean; error?: string }> {
    const { data, error } = await chSupabase.rpc('ch_token_request_changes', {
      p_token: token,
      p_post_id: postId,
      p_actor_name: actorName,
      p_comment: comment,
    });

    if (error) throw error;
    return data as { success: boolean; error?: string };
  },

  async addComment(token: string, postId: string, actorName: string, comment: string): Promise<{ success: boolean; error?: string }> {
    const { data, error } = await chSupabase.rpc('ch_token_add_comment', {
      p_token: token,
      p_post_id: postId,
      p_actor_name: actorName,
      p_comment: comment,
    });

    if (error) throw error;
    return data as { success: boolean; error?: string };
  },

  async getComments(token: string, postId: string): Promise<{ success: boolean; comments?: any[]; error?: string }> {
    const { data, error } = await chSupabase.rpc('ch_token_get_comments', {
      p_token: token,
      p_post_id: postId,
    });

    if (error) throw error;
    return data as { success: boolean; comments?: any[]; error?: string };
  },
};
